import React, { useState } from 'react';
import { telHref } from '../shared/format';
import PanelTakeaway from '../shared/PanelTakeaway';
import InfoTip from '../shared/InfoTip';
import { BTN, BTN_PRIMARY, CARD_LIST, CARD_TITLE, PILL } from './liStyles';

// Booking requests — a worklist that empties. Each request shows who it is for, when,
// its state, and — while it still needs a call — the call script with a Call button.
// Staff record what happened with one tap; a request that is booked, could not be
// booked or was cancelled leaves the list.
const OPEN_STATES = new Set(['draft', 'sent', 'awaiting', 'unreachable', 'alternative_offered']);
const OUTCOMES = [
  { key: 'confirmed', label: 'Booked' },
  { key: 'awaiting', label: 'Waiting to hear back' },
  { key: 'alternative_offered', label: 'Other time offered' },
  { key: 'unreachable', label: 'No answer' },
  { key: 'declined', label: 'Could not book' },
  { key: 'cancelled', label: 'Cancel' },
];

const statePill = (row) => {
  if (row.overdue) return PILL.red;
  if (row.state === 'confirmed') return PILL.emerald;
  if (row.state === 'declined' || row.state === 'unreachable') return PILL.amber;
  return PILL.gray;
};

export const RequestRow = ({ row, onRecord, onOpenPlace }) => {
  const [busy, setBusy] = useState(null);
  if (!row) return null;
  const tel = telHref(row.phone);
  const open = OPEN_STATES.has(row.state);
  const record = async (outcome) => {
    setBusy(outcome);
    try { await onRecord(row.request_id, outcome); } finally { setBusy(null); }
  };
  return (
    <div className="px-4 sm:px-5 py-4 space-y-2" data-testid="li-request">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <button type="button" className="text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-[#32B1FF] text-left" onClick={() => onOpenPlace && onOpenPlace(row.entity_id, row.business)}>
            {row.business}
          </button>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {[row.when_label, row.party_size ? `party of ${row.party_size}` : null, row.channel_label].filter(Boolean).join(' · ')}
          </div>
          {row.detail && <div className="text-sm text-gray-500 dark:text-gray-400">{row.detail}</div>}
        </div>
        {row.state_label && <span className={statePill(row)}>{row.overdue ? `Overdue · ${row.state_label}` : row.state_label}</span>}
      </div>
      {row.call_script && (
        <blockquote className="border-l-4 border-[#32B1FF] bg-[#32B1FF]/5 rounded-r-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200">
          {row.call_script}
        </blockquote>
      )}
      {open && (
        <div className="flex flex-wrap gap-1.5">
          {tel && <a href={tel} className={`${BTN_PRIMARY} !px-2.5 !py-1`}>Call {row.phone}</a>}
          {row.booking_url && <a href={row.booking_url} target="_blank" rel="noopener noreferrer" className={`${BTN} !px-2.5 !py-1`}>Book online</a>}
          {OUTCOMES.filter((o) => o.key !== row.state).map((o) => (
            <button key={o.key} type="button" className={`${BTN} !px-2.5 !py-1`} disabled={Boolean(busy)} onClick={() => record(o.key)}>
              {busy === o.key ? 'Saving…' : o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const RequestsWorklist = ({ panel, onRecord, onOpenPlace, onOpenDrill }) => {
  const rows = Array.isArray(panel?.facts?.rows) ? panel.facts.rows : [];
  const shown = rows.slice(0, 5);
  // Item 1 — the expanded panel renders whenever there are open requests, not
  // only once rows[] have loaded. `sample_size` (panel-level, the open-request
  // count from fn_li_page) is the source of truth; rows[] may briefly lag it.
  // When the count is positive but rows are still empty, the list shows
  // placeholder cards rather than an empty box (work-page-fixes Item 1 §B).
  const sampleSize = Number(panel?.sample_size) || 0;
  const total = Math.max(sampleSize, rows.length);
  const expanded = sampleSize > 0 || rows.length > 0;
  const title = 'Booking Requests';
  const infoTip = 'Tables and bookings the desk is arranging for guests. Record what happened after each call.';
  return (
    <section id="panel-requests" className={CARD_LIST} aria-label={title}>
      {!expanded ? (
        /* Compact "all clear" strip (work-page-fixes Item 1 §B): a single slim row —
           title, the "no requests" message, and the "See every request" link right-
           aligned on the same line. No empty panel, no wasted vertical space. The
           strip is roughly the height of a sublabel line, not a padded card with
           blank space. The title uses the same CARD_TITLE text-lg treatment as the
           "What Does the Guest Need?" title so every panel heading matches. */
        <div className="flex flex-col items-start sm:flex-row sm:items-center gap-1 sm:gap-3 px-4 sm:px-5 py-2.5">
          <h2 className={`${CARD_TITLE} text-lg shrink-0`}>
            {title}
            <InfoTip text={infoTip} />
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 sm:truncate">No booking requests right now.</p>
          {panel?.facts?.drill && (
            <button
              type="button"
              className="ml-auto shrink-0 text-sm font-medium text-[#32B1FF] hover:underline"
              onClick={() => onOpenDrill(panel.facts.drill)}
            >
              See every request
            </button>
          )}
        </div>
      ) : (
        /* Expanded panel (open-request count > 0): title + takeaway (the
           description line), the request list, then the "See all N" link. When
           rows[] haven't arrived yet, the list renders placeholder cards so the
           panel never reads as an empty box (work-page-fixes Item 1 §B). */
        <>
          <div className="px-4 sm:px-5 pt-4">
            <h2 className={`${CARD_TITLE} text-lg mb-2`}>
              {title}
              <InfoTip text={infoTip} />
            </h2>
            <PanelTakeaway sentence={panel?.sentence} />
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-800 border-t border-gray-100 dark:border-gray-800">
            {shown.length > 0 ? (
              shown.map((r) => <RequestRow key={r.request_id} row={r} onRecord={onRecord} onOpenPlace={onOpenPlace} />)
            ) : (
              /* Item 1 — count is positive but rows[] haven't landed yet: a
                 graceful placeholder so the panel never reads as an empty box.
                 One dashed placeholder card per expected row (capped at 3); the
                 real RequestRow rendering takes over the moment rows arrive. */
              <div className="px-4 sm:px-5 py-4 space-y-3">
                {Array.from({ length: Math.min(Math.max(total, 1), 3) }).map((_, i) => (
                  <div key={i} className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-3 animate-pulse">
                    <div className="h-3.5 w-1/3 rounded bg-gray-200 dark:bg-gray-700 mb-2" />
                    <div className="h-3 w-2/3 rounded bg-gray-200 dark:bg-gray-700" />
                  </div>
                ))}
              </div>
            )}
          </div>
          {panel?.facts?.drill && (
            <div className="px-4 sm:px-5 py-3 border-t border-gray-100 dark:border-gray-800">
              <button type="button" className="text-sm font-medium text-[#32B1FF] hover:underline" onClick={() => onOpenDrill(panel.facts.drill)}>
                {total > shown.length ? `See all ${total}` : 'See every request'}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default RequestsWorklist;
